import { useConnectedChannels } from '@/composables/useConnectedChannels';
import { isDevOnlyPagesVisible } from '@/composables/useDevOnlyOverride';
import { useAuthStore } from '@/stores/auth';
import { useEnvironmentStore } from '@/stores/environment';

// The single env/super-admin gate for nav items, shared by VerticalNavLink,
// VerticalNavGroup and the mobile header Menu (previously triplicated in each).
//
// Flag semantics:
// - `superAdminOnly` hides an item from non-super-admins in every environment.
// - `superAdminVisible` shows an env-gated item to super admins even when the env
//   gate fails (Dev Tools doubles as the internal ops console on production builds).
// - `envOnly: 'development' | 'production'` shows the item only in that effective
//   environment. `ignoreEnvSimulation` items (Dev Tools) gate on the REAL detected
//   env so the env toggle can never hide itself, while still vanishing on a real
//   production build.
// - envOnly:'development' items follow the Dev Settings "Show dev-only pages" switch
//   (isDevOnlyPagesVisible): on by default in development, where turning it off hides them;
//   elsewhere it reveals them to super admins only. The matching route guard is
//   isDevOnlyPathAllowed (router/devOnlyPaths.js).
//
// Call `isEnvAllowed` directly from the template v-if (not via a computed) — it must
// re-evaluate during render, same as the CASL `can()` helpers it composes with.
//
// `isDevOnly` is purely cosmetic: it tints a rendered dev-only item so an unreleased
// page is distinguishable from a shipped one at a glance. It deliberately keys off the
// flag alone, not the environment — so the tint also marks the items a super admin
// reveals on prod/stage via Dev Settings → "Show dev-only pages", which is exactly
// when confusing the two is most costly. `ignoreEnvSimulation` items (Dev Tools) are
// excluded: the whole section is internal, so tinting it carries no information.
export const useNavItemVisibility = () => {
  const auth = useAuthStore();
  const env = useEnvironmentStore();

  const isDevOnly = (item) => item?.envOnly === 'development' && !item?.ignoreEnvSimulation;

  const isEnvAllowed = (item) => {
    const isSuperAdmin = !!auth.user?.superAdmin;
    if (item?.superAdminOnly && !isSuperAdmin) return false;
    if (item?.superAdminVisible && isSuperAdmin) return true;
    if (!item?.envOnly) return true;
    if (item.envOnly === 'development' && !item.ignoreEnvSimulation) return isDevOnlyPagesVisible(auth, env);
    const isDev = item.ignoreEnvSimulation ? env.detectedEnvironment === 'development' : env.isDevelopment;
    const isProd = item.ignoreEnvSimulation ? env.detectedEnvironment === 'production' : env.isProduction;
    if (item.envOnly === 'development') return isDev;
    if (item.envOnly === 'production') return isProd;
    return true;
  };

  // `channel` gate: an item tagged `channel: '<key>'` shows only once that marketplace is
  // connected for the current business profile (see useConnectedChannels). PURE — safe in a
  // template v-if — and hidden until the connection state is known, so a group never
  // flashes in and out. Built lazily so merely calling useNavItemVisibility() (specs, items
  // with no channel) does not touch the account store.
  let connectedChannels = null;
  const isChannelAllowed = (item) => {
    if (!item?.channel) return true;
    connectedChannels ??= useConnectedChannels({ isEnvAllowed });
    return connectedChannels.isChannelReady(item.channel) && connectedChannels.isConnected(item.channel);
  };

  return { isEnvAllowed, isChannelAllowed, isDevOnly };
};
